// ---------------------------------------------------------------------------
// Putting a cart that is still waiting onto paper.
//
// WHAT THIS MUST NOT DO, which is most of its design:
//
//   it does not complete the sale, take a payment, deduct stock, reserve
//   stock, touch the shift totals, or move the cart off the spike. Nothing
//   here writes to the held order at all - it reads it, photographs a sheet,
//   and posts the pixels.
//
// The job it creates has its own document type and its own origin, so it can
// never collide with, or be counted as, the ORIGINAL receipt the sale will owe
// if this cart is eventually paid for. Pressing the button twice is two sheets
// on purpose: the customer asked for another copy.
//
// The sheet is mounted only while a print is in flight. A drawer listing ten
// held carts would otherwise carry ten hidden receipts around with it, each
// laying out and decoding a logo for a button nobody pressed.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { Printer, RotateCcw } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '../../i18n/I18nProvider';
import { usePrintConfig } from '../../api/designs.api';
import { usePrinterDevices } from '../../api/printerDevices.api';
import { usePublicSettings } from '../../api/system.api';
import { useNetworkReprint } from '../../hooks/useNetworkReprint';
import { HeldOrderSheet } from '../print/HeldOrderSheet';
import { FALLBACK_RECEIPT_CONFIG } from '../../types/design';
import { toast } from '../../stores/toastStore';
import type { HeldOrder } from '../../types';
import type { CSSProperties } from 'react';

/// Off the side of the screen, still laid out and still decoding images. Not
/// `display: none`, which lays nothing out and decodes nothing.
const OFF_SCREEN: CSSProperties = {
  position: 'fixed',
  insetInlineStart: '-200vw',
  top: 0,
  background: '#ffffff',
};

export function PrintHeldOrderButton({
  held,
  className,
}: {
  held: HeldOrder;
  className?: string;
}) {
  const { t } = useI18n();
  const sheet = useRef<HTMLDivElement>(null);
  const print = useNetworkReprint();

  /// True from the press until the pixels have been posted. It is what mounts
  /// the sheet, so the effect below has something to photograph.
  const [armed, setArmed] = useState(false);

  const { data: config } = usePrintConfig();
  const { data: settings } = usePublicSettings();
  // The sheet a customer looks at before paying comes out at the counter,
  // which is wherever the shop has said its receipts print.
  const { data: printers } = usePrinterDevices();
  const receiptPrinter = printers?.find((row) => row.isDefaultReceipt);

  const receipt = config?.receipt ?? FALLBACK_RECEIPT_CONFIG;
  const cashierPrinter = receiptPrinter;
  const ready = Boolean(cashierPrinter?.enabled && cashierPrinter.ipAddress);

  useEffect(() => {
    if (!armed) return;

    // One frame, so the sheet is laid out before it is captured.
    const frame = requestAnimationFrame(() => {
      void (async () => {
        const status = await print.send({
          printerRole: 'CASHIER_RECEIPT',
          printerProfileId: receiptPrinter?.id ?? null,
          documentType: 'HELD_ORDER_SHEET',
          heldOrderId: held.id,
          element: () => sheet.current?.querySelector<HTMLElement>('.held-order-sheet') ?? null,
          widthMm: receipt.widthMm === 58 ? 58 : 80,
        });

        setArmed(false);

        // "Sent to printer" is as far as this can honestly go: a raw socket
        // returns no acknowledgement that paper came out.
        if (status === 'SENT') toast.success(t.heldSheet.sent);
        else if (status === 'NO_AGENT') toast.error(t.salePrint.agentSilentToast, t.salePrint.agentSilentDetail);
        else if (status === 'QUEUED' || status === 'PRINTING') toast.info(t.salePrint.onItsWay);
        else toast.error(t.heldSheet.failed, t.heldSheet.unchanged);
      })();
    });

    return () => cancelAnimationFrame(frame);
    // `armed` is the trigger; re-running on anything else would print twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed]);

  const failed = print.status === 'FAILED';

  return (
    <>
      <button
        type="button"
        className={clsx('btn-secondary min-h-9 gap-1.5 text-xs', className)}
        disabled={!ready || armed}
        title={ready ? undefined : t.heldSheet.noPrinter}
        onClick={() => setArmed(true)}
      >
        {failed ? (
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <Printer className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {armed ? t.heldSheet.printing : failed ? t.heldSheet.retry : t.heldSheet.print}
      </button>

      {/* Said beside the button as well as in the toast: a cashier who looked
          away needs to find out from the screen, not from the customer. */}
      {failed && !armed ? (
        <p className="w-full text-xs text-burgundy-red" role="alert">
          {t.heldSheet.failed}
        </p>
      ) : null}

      {armed ? (
        <div aria-hidden="true" style={OFF_SCREEN}>
          <div ref={sheet}>
            <HeldOrderSheet
              config={receipt}
              held={held}
              forPreview
              shop={{
                nameEn: settings?.['business.name_en'] ?? 'Yaghi Butcher',
                nameAr: settings?.['business.name_ar'] ?? 'ملحمة ياغي',
                address: settings?.['business.address'] ?? '',
                phone: settings?.['business.phone'] ?? '',
              }}
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
